import billingPolicy from '../config/billingPolicy.json';
import React, { useState, useEffect, useMemo } from "react";
import { getDocuments, addData, deleteData, updateUserSettings, getAllUsers, getPresets } from "../utils/firestoreService";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { Search, Plus, Edit, Building, Trash2, HelpCircle, Copy, Eye, EyeOff, CreditCard, Users, Tag, X, ChevronDown, FileText } from "lucide-react";
import { defaultCompanyFeatureFlags, defaultCompanyFlagMinRoles, companyFlags, ROLE_HIERARCHY, ROLE_LABELS } from "../config/featureFlagDefinitions";
import { findMissingFeaturePreferenceUsers } from "../utils/featureFlags";
import { collection, doc, getDocs, runTransaction } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useUnsavedChanges } from './useUnsavedChanges';
import { sortByDisplayLabel } from './sortByDisplayLabel';
import { useAdminSessionState } from './useAdminSessionState';
import { useAdminFeedback } from './useAdminFeedback';

// Subcategory definitions — mirrors AdminDocumentTypeEditor.js
const DOC_TYPE_SUBCATEGORIES = [
  { value: "initial-note", label: "Initial Note", category: 1 },
  { value: "psychiatric-progress", label: "Psychiatric Progress Note", category: 1 },
  { value: "psychotherapy-progress", label: "Psychotherapy Progress Note", category: 1 },
  { value: "assessment", label: "Assessment Note", category: 1 },
  { value: "care-coordination", label: "Care Coordination Note", category: 1 },
  { value: "document-importing", label: "Document Importing", category: 2 },
  { value: "summary-document", label: "Summary Document", category: 3 },
];

const isLitigationHoldActive = (company) => (
  typeof company?.litigationHold === 'boolean'
    ? company.litigationHold
    : company?.litigationHold?.active === true
);

const AdminCompanies = ({ adminPrefs, setAdminPrefs, userId }) => {
  const { success: notifySuccess, copy: copyValue } = useAdminFeedback();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useAdminSessionState(`admin:${userId}:companiesSearch`, '');
  const [sortConfig, setSortConfig] = useState({
    key: adminPrefs?.companiesSortKey === 'requiresPayment' ? 'name' : (adminPrefs?.companiesSortKey ?? 'name'),
    direction: adminPrefs?.companiesSortDirection ?? 'asc'
  });
  
  // Enhanced filter states
  const [showInactive, setShowInactive] = useState(adminPrefs?.companiesShowInactive ?? true);
  const [typeFilter, setTypeFilter] = useState(adminPrefs?.companiesTypeFilter ?? 'all');
  const [paymentFilter, setPaymentFilter] = useState(billingPolicy.enabled ? (adminPrefs?.companiesPaymentFilter ?? 'all') : 'all'); // 'all', 'required', 'not_required'
  const [usersFilter, setUsersFilter] = useState(adminPrefs?.companiesUsersFilter ?? 'all'); // 'all', 'has_users', 'no_users'
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [holdReleaseConfirmOpen, setHoldReleaseConfirmOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  // Template presets for company feature flags (fetched from Firestore)
  const [companyPresets, setCompanyPresets] = useState({});
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [enableAiForMissingUsers, setEnableAiForMissingUsers] = useState(false);
  // Document type assignment
  const [allDocumentTypes, setAllDocumentTypes] = useState([]);
  const [docTypesLoading, setDocTypesLoading] = useState(false);
  const [docTypePresets, setDocTypePresets] = useState({});
  const [selectedDocTypePresetId, setSelectedDocTypePresetId] = useState('');
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    type: "",
    isActive: true,
    requiresPayment: false,
    domain: "",
    email: "",
    phone: "",
    description: "",
    litigationHoldActive: false,
    // Feature Flags (three-layer gate — company layer)
    featureFlags: { ...defaultCompanyFeatureFlags },
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const hasUnsavedCompanyChanges = dialogOpen && initialFormData !== null && JSON.stringify(formData) !== initialFormData;
  const confirmDiscardCompanyChanges = useUnsavedChanges(hasUnsavedCompanyChanges);
  const openWithFormData = next => {
    setFormData(next);
    setInitialFormData(JSON.stringify(next));
  };

  useEffect(() => {
    fetchCompanies();
    // Fetch all company template presets once on mount
    getPresets('company').then((presets) => {
      if (presets) setCompanyPresets(presets);
    });
    // Fetch document type template presets
    getPresets('documentTypes').then((presets) => {
      if (presets) setDocTypePresets(presets);
    });
    // Fetch all document types for the assignment checklist
    fetchAllDocumentTypes();
  }, []);

  const fetchAllDocumentTypes = async () => {
    setDocTypesLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'documentTypes'));
      const types = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      types.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAllDocumentTypes(types);
    } catch (error) {
      console.error('Error fetching document types:', error);
    }
    setDocTypesLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const [companiesData, usersData] = await Promise.all([
        getDocuments('companies'),
        getAllUsers()
      ]);
      setCompanies(companiesData);
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
    setIsLoading(false);
  };

  // Compute user stats per company ID
  const companyStats = useMemo(() => {
    const stats = {};
    users.forEach(user => {
      const companyId = user.companyId;
      if (!companyId) return;
      if (!stats[companyId]) {
        stats[companyId] = { userCount: 0, lastLogin: null, invitesSent: 0 };
      }
      stats[companyId].userCount++;
      stats[companyId].invitesSent += user.invitesSent || 0;
      // Track latest login
      const loginTime = user.lastLoginTime?.toDate ? user.lastLoginTime.toDate() : (user.lastLoginTime ? new Date(user.lastLoginTime) : null);
      if (loginTime && (!stats[companyId].lastLogin || loginTime > stats[companyId].lastLogin)) {
        stats[companyId].lastLogin = loginTime;
      }
    });
    return stats;
  }, [users]);

  const handleOpenDialog = (company = null) => {
    setSaveError('');
    setDialogTab(0); // Reset to Details tab
    setSelectedPresetId(''); // Reset preset selector
    setEnableAiForMissingUsers(false);
    if (company) {
      setEditingCompany(company);
      openWithFormData({
        name: company.name || company.companyName || "",
        code: company.code || "",
        type: company.type || "",
        isActive: company.isActive !== undefined ? company.isActive : true,
        requiresPayment: company.requiresPayment || false,
        createCompanyOnSignup: company.createCompanyOnSignup || false,
        domain: company.domain || "",
        email: company.email || "",
        phone: company.phone || "",
        description: company.description || "",
        litigationHoldActive: isLitigationHoldActive(company),
        // Feature Flags
        featureFlags: { ...defaultCompanyFeatureFlags, ...company.featureFlags },
        flagMinRoles: { ...defaultCompanyFlagMinRoles, ...company.flagMinRoles },
        // Document type assignment (null = all types visible, array = allowlist)
        enabledDocumentTypes: company.enabledDocumentTypes ?? null,
      });
    } else {
      // New company — use Firestore default template preset if available, else code defaults
      const defaultPreset = Object.values(companyPresets).find(p => p.isDefault);
      const presetFlags = defaultPreset?.featureFlags ?? defaultCompanyFeatureFlags;
      const presetMinRoles = defaultPreset?.flagMinRoles ?? defaultCompanyFlagMinRoles;
      setEditingCompany(null);
      openWithFormData({
        name: "",
        code: "",
        type: "",
        isActive: true,
        requiresPayment: false,
        createCompanyOnSignup: false,
        domain: "",
        email: "",
        phone: "",
        description: "",
        litigationHoldActive: false,
        // Feature Flags from template or code defaults
        featureFlags: { ...defaultCompanyFeatureFlags, ...presetFlags },
        flagMinRoles: { ...defaultCompanyFlagMinRoles, ...presetMinRoles },
        // Document type assignment — open default for new companies
        enabledDocumentTypes: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = (force = false) => {
    if (!force && !confirmDiscardCompanyChanges()) return;
    setDialogOpen(false);
    setEditingCompany(null);
    setEnableAiForMissingUsers(false);
    setHoldReleaseConfirmOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handler for nested featureFlags map
  const handleFeatureFlagChange = (flagName) => (e) => {
    const { checked } = e.target;
    setFormData(prev => ({
      ...prev,
      featureFlags: {
        ...prev.featureFlags,
        [flagName]: checked
      }
    }));
  };

  // Document type assignment handlers
  const docTypesBySubcategory = useMemo(() => {
    const grouped = {};
    DOC_TYPE_SUBCATEGORIES.forEach(sub => {
      grouped[sub.value] = {
        label: sub.label,
        category: sub.category,
        types: []
      };
    });
    // Group types by subcategory; types without a subcategory go to a catch-all
    allDocumentTypes.forEach(type => {
      const subcat = type.subcategory || 'initial-note';
      if (grouped[subcat]) {
        grouped[subcat].types.push(type);
      } else {
        // Fallback: put in initial-note group
        grouped['initial-note'].types.push(type);
      }
    });
    return grouped;
  }, [allDocumentTypes]);

  const enabledDocTypesCount = useMemo(() => {
    if (!formData.enabledDocumentTypes) return allDocumentTypes.length;
    return formData.enabledDocumentTypes.length;
  }, [formData.enabledDocumentTypes, allDocumentTypes.length]);

  const isDocTypeEnabled = (typeId) => {
    // null = all enabled (open default)
    if (!formData.enabledDocumentTypes) return true;
    return formData.enabledDocumentTypes.includes(typeId);
  };

  const handleDocTypeToggle = (typeId) => {
    setFormData(prev => {
      let current = prev.enabledDocumentTypes;
      if (current === null) {
        // First uncheck: convert from null (all) to explicit list minus this one
        current = allDocumentTypes.map(t => t.id).filter(id => id !== typeId);
      } else if (current.includes(typeId)) {
        // Uncheck: remove from list
        current = current.filter(id => id !== typeId);
      } else {
        // Check: add to list
        current = [...current, typeId];
      }
      // If all types are now checked, reset to null (open default)
      if (current.length === allDocumentTypes.length) {
        current = null;
      }
      return { ...prev, enabledDocumentTypes: current };
    });
  };

  const handleSubcategoryToggle = (subcategoryValue) => {
    const group = docTypesBySubcategory[subcategoryValue];
    if (!group || group.types.length === 0) return;
    const groupIds = group.types.map(t => t.id);
    const allEnabled = groupIds.every(id => isDocTypeEnabled(id));

    setFormData(prev => {
      let current = prev.enabledDocumentTypes;
      if (current === null) {
        // Currently all enabled — toggling a group off means explicit list minus this group
        current = allDocumentTypes.map(t => t.id).filter(id => !groupIds.includes(id));
      } else if (allEnabled) {
        // All in group are on — turn them all off
        current = current.filter(id => !groupIds.includes(id));
      } else {
        // Some off — turn them all on
        const currentSet = new Set(current);
        groupIds.forEach(id => currentSet.add(id));
        current = Array.from(currentSet);
      }
      if (current.length === allDocumentTypes.length) {
        current = null;
      }
      return { ...prev, enabledDocumentTypes: current };
    });
  };

  const handleSelectAllDocTypes = () => {
    setFormData(prev => ({ ...prev, enabledDocumentTypes: null }));
  };

  const handleClearAllDocTypes = () => {
    setFormData(prev => ({ ...prev, enabledDocumentTypes: [] }));
  };

  // Handler for per-flag minRole overrides
  const handleFlagMinRoleChange = (flagKey, value) => {
    setFormData(prev => ({
      ...prev,
      flagMinRoles: {
        ...prev.flagMinRoles,
        [flagKey]: value || null
      }
    }));
  };

  const missingAiUsers = useMemo(() => {
    if (!editingCompany || formData.featureFlags?.aiAssistant !== true) return [];
    return findMissingFeaturePreferenceUsers(users, {
      companyId: editingCompany.id,
      userSettingKey: 'useAiAssistant',
      minRole: formData.flagMinRoles?.aiAssistant,
    });
  }, [editingCompany, formData.featureFlags?.aiAssistant, formData.flagMinRoles?.aiAssistant, users]);

  const handleSave = async (holdReleaseConfirmed = false) => {
    holdReleaseConfirmed = holdReleaseConfirmed === true;
    if (!formData.name || !formData.code) {
      return; // Basic validation
    }

    const previousHoldActive = isLitigationHoldActive(editingCompany);
    const holdChanged = !!editingCompany
      && previousHoldActive !== formData.litigationHoldActive;
    if (previousHoldActive && !formData.litigationHoldActive && !holdReleaseConfirmed) {
      setHoldReleaseConfirmOpen(true);
      return;
    }

    setSaveError('');
    setIsSaving(true);
    let companySaved = false;
    try {
      const dataToSave = {
        name: formData.name,
        code: formData.code,
        type: formData.type || null,
        isActive: formData.isActive,
        requiresPayment: formData.requiresPayment,
        createCompanyOnSignup: formData.createCompanyOnSignup,
        domain: formData.domain || null,
        email: formData.email || null,
        phone: formData.phone || null,
        description: formData.description || null,
        litigationHold: holdChanged
          ? {
              active: formData.litigationHoldActive,
              updatedAt: new Date(),
              updatedBy: userId,
            }
          : (editingCompany?.litigationHold || { active: false }),
        // Feature Flags (three-layer gate)
        featureFlags: formData.featureFlags,
        flagMinRoles: formData.flagMinRoles,
        // Document type assignment (null = all, array = allowlist)
        enabledDocumentTypes: formData.enabledDocumentTypes,
        updatedAt: new Date()
      };

      if (editingCompany) {
        const companyRef = doc(db, 'companies', editingCompany.id);
        const auditRef = doc(collection(db, 'auditLogs'));
        await runTransaction(db, async (transaction) => {
          transaction.update(companyRef, dataToSave);
          if (holdChanged) {
            transaction.set(auditRef, {
              action: formData.litigationHoldActive
                ? 'litigation_hold.activate'
                : 'litigation_hold.release',
              userId,
              companyId: editingCompany.id,
              resourceType: 'company',
              resourceId: editingCompany.id,
              previousValue: previousHoldActive,
              newValue: formData.litigationHoldActive,
              timestamp: new Date(),
            });
          }
        });
        setCompanies(prev => prev.map(c => 
          c.id === editingCompany.id ? { ...c, ...dataToSave } : c
        ));
        companySaved = true;

        if (enableAiForMissingUsers && missingAiUsers.length > 0) {
          const updatedIds = new Set();
          for (let offset = 0; offset < missingAiUsers.length; offset += 20) {
            const results = await Promise.all(
              missingAiUsers.slice(offset, offset + 20).map((user) => (
                runTransaction(db, async (transaction) => {
                  const userRef = doc(db, 'users', user.id);
                  const snapshot = await transaction.get(userRef);
                  if (!snapshot.exists()) return null;

                  // Recheck inside the transaction so a newly saved explicit
                  // opt-out or role/disabled change cannot be overwritten.
                  const [stillEligible] = findMissingFeaturePreferenceUsers([
                    { id: snapshot.id, ...snapshot.data() },
                  ], {
                    companyId: editingCompany.id,
                    userSettingKey: 'useAiAssistant',
                    minRole: formData.flagMinRoles?.aiAssistant,
                  });
                  if (!stillEligible) return null;

                  transaction.update(userRef, { 'settings.useAiAssistant': true });
                  return user.id;
                })
              )),
            );
            results.filter(Boolean).forEach((id) => updatedIds.add(id));
          }
          setUsers(prev => prev.map((user) => (
            updatedIds.has(user.id)
              ? { ...user, settings: { ...user.settings, useAiAssistant: true } }
              : user
          )));
        }
      } else {
        dataToSave.createdAt = new Date();
        const newId = await addData('companies', dataToSave);
        setCompanies(prev => [...prev, { id: newId, ...dataToSave }]);
      }

      handleCloseDialog(true);
      notifySuccess(editingCompany ? 'Company settings saved.' : 'Company created.');
    } catch (error) {
      console.error("Error saving company:", error);
      setSaveError(companySaved
        ? 'Company settings were saved, but some user preferences could not be updated. Your edits are still here; retry Save.'
        : 'Could not save company settings. Your edits are still here. Check your connection and retry Save.');
    }
    setIsSaving(false);
  };

  const filteredCompanies = companies.filter(company => {
    // Filter by active status
    if (!showInactive && company.isActive === false) return false;
    
    // Filter by type
    if (typeFilter !== 'all') {
      if (typeFilter === 'none') {
        if (company.type) return false;
      } else {
        if (company.type !== typeFilter) return false;
      }
    }
    
    // Filter by payment requirement
    if (billingPolicy.enabled && paymentFilter !== 'all') {
      if (paymentFilter === 'required' && !company.requiresPayment) return false;
      if (paymentFilter === 'not_required' && company.requiresPayment) return false;
    }
    
    // Filter by users (needs companyStats)
    if (usersFilter !== 'all') {
      const hasUsers = (companyStats[company.id]?.userCount || 0) > 0;
      if (usersFilter === 'has_users' && !hasUsers) return false;
      if (usersFilter === 'no_users' && hasUsers) return false;
    }
    
    // Filter by search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      company.name?.toLowerCase().includes(query) ||
      company.companyName?.toLowerCase().includes(query) ||
      company.code?.toLowerCase().includes(query) ||
      company.type?.toLowerCase().includes(query)
    );
  });

  const handleRequestSort = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortConfig({ key: property, direction: newDirection });
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, companiesSortKey: property, companiesSortDirection: newDirection };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  const sortedCompanies = useMemo(() => {
    return [...filteredCompanies].sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case 'name':
          aValue = (a.name || a.companyName || '').toLowerCase();
          bValue = (b.name || b.companyName || '').toLowerCase();
          break;
        case 'code':
          aValue = (a.code || '').toLowerCase();
          bValue = (b.code || '').toLowerCase();
          break;
        case 'type':
          aValue = (a.type || 'zzz').toLowerCase();
          bValue = (b.type || 'zzz').toLowerCase();
          break;
        case 'isActive':
          aValue = a.isActive !== false ? 1 : 0;
          bValue = b.isActive !== false ? 1 : 0;
          break;
        case 'userCount':
          aValue = companyStats[a.id]?.userCount || 0;
          bValue = companyStats[b.id]?.userCount || 0;
          break;
        case 'invitesSent':
          aValue = companyStats[a.id]?.invitesSent || 0;
          bValue = companyStats[b.id]?.invitesSent || 0;
          break;
        case 'lastLogin':
          aValue = companyStats[a.id]?.lastLogin?.getTime() || 0;
          bValue = companyStats[b.id]?.lastLogin?.getTime() || 0;
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCompanies, sortConfig, companyStats]);

  // Get unique company types for filter dropdown
  const uniqueTypes = useMemo(() => {
    const types = new Set();
    companies.forEach(c => {
      if (c.type) types.add(c.type);
    });
    return sortByDisplayLabel(Array.from(types), type => type);
  }, [companies]);

  // Helper to persist filter preferences
  const updateFilterPref = (key, value) => {
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, [key]: value };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  // Count active filters for indicator
  const activeFilterCount = [
    !showInactive,
    typeFilter !== 'all',
    billingPolicy.enabled && paymentFilter !== 'all',
    usersFilter !== 'all'
  ].filter(Boolean).length;

  // Clear all filters
  const clearAllFilters = () => {
    setShowInactive(true);
    setTypeFilter('all');
    setPaymentFilter('all');
    setUsersFilter('all');
    if (userId && setAdminPrefs) {
      const updatedPrefs = { 
        ...adminPrefs, 
        companiesShowInactive: true,
        companiesTypeFilter: 'all',
        companiesPaymentFilter: 'all',
        companiesUsersFilter: 'all'
      };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  // Styled filter button helper
  const FilterIconButton = ({ active, onClick, tooltip, color = 'var(--color-info-text)', children }) => (
    <Tooltip title={tooltip} arrow>
      <IconButton 
        onClick={onClick}
        size="small"
        sx={{ 
          color: active ? color : 'var(--color-text-secondary)',
          backgroundColor: active ? `${color}15` : 'transparent',
          '&:hover': {
            backgroundColor: active ? `${color}25` : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );

  if (isLoading) {
    return (
      <div className="standard-container">
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="standard-container" style={{ marginBottom: '60px' }}>
      {/* Header Row: Search + Filter Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        {/* Search */}
        <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
          <Tooltip title="Search companies" arrow>
            <Search className="header-search-icon" size={18} />
          </Tooltip>
          <input
            type="text"
            placeholder="Search Companies"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="header-search-input"
          />
        </div>

        {/* Filter Controls Group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Active/Inactive Toggle */}
          <FilterIconButton
            active={!showInactive}
            onClick={() => {
              const newValue = !showInactive;
              setShowInactive(newValue);
              updateFilterPref('companiesShowInactive', newValue);
            }}
            tooltip={showInactive ? "Hide inactive companies" : "Show inactive companies"}
          >
            {showInactive ? <Eye size={18} /> : <EyeOff size={18} />}
          </FilterIconButton>

          {/* Type Filter */}
          <Select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              updateFilterPref('companiesTypeFilter', e.target.value);
            }}
            size="small"
            displayEmpty
            sx={{ 
              minWidth: 100, 
              fontSize: 'var(--text-xs)',
              '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }
            }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tag size={14} style={{ opacity: value !== 'all' ? 1 : 0.5 }} />
                <span>{value === 'all' ? 'All Types' : value === 'none' ? 'No Type' : value}</span>
              </Box>
            )}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="none">No Type</MenuItem>
            {uniqueTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>

          {/* Payment Filter */}
          {billingPolicy.enabled && <Select
            value={paymentFilter}
            onChange={(e) => {
              setPaymentFilter(e.target.value);
              updateFilterPref('companiesPaymentFilter', e.target.value);
            }}
            size="small"
            displayEmpty
            sx={{ 
              minWidth: 120, 
              fontSize: 'var(--text-xs)',
              '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }
            }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CreditCard size={14} style={{ opacity: value !== 'all' ? 1 : 0.5 }} />
                <span>{value === 'all' ? 'All Payment' : value === 'required' ? 'Payment Req' : 'No Payment'}</span>
              </Box>
            )}
          >
            <MenuItem value="all">All Payment</MenuItem>
            <MenuItem value="required">Payment Required</MenuItem>
            <MenuItem value="not_required">No Payment Required</MenuItem>
          </Select>}

          {/* Users Filter */}
          <Select
            value={usersFilter}
            onChange={(e) => {
              setUsersFilter(e.target.value);
              updateFilterPref('companiesUsersFilter', e.target.value);
            }}
            size="small"
            displayEmpty
            sx={{ 
              minWidth: 100, 
              fontSize: 'var(--text-xs)',
              '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }
            }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Users size={14} style={{ opacity: value !== 'all' ? 1 : 0.5 }} />
                <span>{value === 'all' ? 'All Users' : value === 'has_users' ? 'Has Users' : 'No Users'}</span>
              </Box>
            )}
          >
            <MenuItem value="all">All Companies</MenuItem>
            <MenuItem value="has_users">Has Users</MenuItem>
            <MenuItem value="no_users">No Users</MenuItem>
          </Select>

          {/* Clear Filters Button */}
          {activeFilterCount > 0 && (
            <Tooltip title="Clear all filters" arrow>
              <Chip
                icon={<X size={14} />}
                label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
                size="small"
                onClick={clearAllFilters}
                onDelete={clearAllFilters}
                color="primary"
                variant="outlined"
                sx={{ ml: 0.5 }}
              />
            </Tooltip>
          )}

          {/* Help + Add Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <Tooltip title="Help" arrow>
              <IconButton onClick={() => setHelpDialogOpen(true)} size="small">
                <HelpCircle size={18} />
              </IconButton>
            </Tooltip>
            <Button 
              variant="contained" 
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => handleOpenDialog()}
              sx={{ ml: 1 }}
            >
              Add Company
            </Button>
          </Box>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1000 }}>
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '9%' }} />
          </colgroup>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortConfig.key === 'name'}
                  direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortConfig.key === 'code'}
                  direction={sortConfig.key === 'code' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('code')}
                >
                  Code
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortConfig.key === 'type'}
                  direction={sortConfig.key === 'type' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                <TableSortLabel
                  active={sortConfig.key === 'isActive'}
                  direction={sortConfig.key === 'isActive' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('isActive')}
                >
                  Active
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                <TableSortLabel
                  active={sortConfig.key === 'userCount'}
                  direction={sortConfig.key === 'userCount' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('userCount')}
                >
                  Users
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                <TableSortLabel
                  active={sortConfig.key === 'lastLogin'}
                  direction={sortConfig.key === 'lastLogin' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('lastLogin')}
                >
                  Last Login
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {searchQuery || activeFilterCount > 0 ? (
                    "No companies match your current filters"
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Building size={48} color="var(--color-text-secondary)" />
                      <span>No companies found. Add your first company to get started.</span>
                      <Button
                        startIcon={<Plus size={16} />}
                        onClick={() => handleOpenDialog()}
                        variant="outlined"
                        size="small"
                      >
                        Add First Company
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              sortedCompanies.map((company) => (
                <TableRow key={company.id} hover>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    <span 
                      style={{ fontWeight: 500, cursor: 'pointer', color: 'var(--color-primary)' }}
                      onClick={() => handleOpenDialog(company)}
                    >
                      {company.name || company.companyName || 'Untitled'}
                    </span>
                    {company.description && (
                      <Tooltip title={company.description} arrow>
                        <span style={{ marginLeft: '6px', color: 'var(--color-text-secondary)', cursor: 'help' }}>ⓘ</span>
                      </Tooltip>
                    )}
                    {isLitigationHoldActive(company) && (
                      <Chip
                        label="Litigation Hold"
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={
                      <>
                        {company.code && <Box component="code" sx={{ display: 'block', overflowWrap: 'anywhere', mb: 0.5 }}>{company.code}</Box>}
                        Click to copy invite code
                      </>
                    } arrow describeChild>
                      <span 
                        style={{ 
                          display: 'inline-flex', 
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          verticalAlign: 'middle',
                          alignItems: 'center', 
                          gap: '4px',
                          cursor: 'pointer',
                          backgroundColor: 'var(--color-background-alt)', 
                          padding: '2px 6px', 
                          borderRadius: '4px'
                        }}
                        role={company.code ? 'button' : undefined}
                        tabIndex={company.code ? 0 : undefined}
                        aria-label={company.code ? `Copy code for ${company.name || company.companyName}` : undefined}
                        onClick={() => company.code && copyValue(company.code, 'Company code')}
                        onKeyDown={event => {
                          if (company.code && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            copyValue(company.code, 'Company code');
                          }
                        }}
                      >
                        <code style={{ fontSize: 'var(--text-sm)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {company.code || '—'}
                        </code>
                        {company.code && <Copy size={12} style={{ opacity: 0.6, flexShrink: 0 }} />}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{company.type || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={company.isActive !== false ? 'Active' : 'Inactive'} 
                      size="small"
                      color={company.isActive !== false ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {companyStats[company.id]?.userCount || 0}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {companyStats[company.id]?.lastLogin 
                      ? companyStats[company.id].lastLogin.toLocaleDateString()
                      : <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                    }
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit company" arrow>
                      <IconButton size="small" onClick={() => handleOpenDialog(company)}>
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete company" arrow>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          setCompanyToDelete(company);
                          setDeleteConfirmText("");
                          setDeleteError('');
                          setDeleteDialogOpen(true);
                        }}
                        sx={{ color: 'error.main', ml: 0.5 }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer with count */}
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          Showing {filteredCompanies.length} of {companies.length} companies
          {(activeFilterCount > 0 || searchQuery) && ' (filtered)'}
        </span>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => handleCloseDialog()}
        maxWidth="sm" 
        fullWidth
        sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      >
        <DialogTitle>{editingCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
        <Tabs 
          value={dialogTab} 
          onChange={(e, newValue) => setDialogTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        >
          <Tab label="Details" />
          <Tab label="Company Settings" />
          <Tab label={`Document Types (${enabledDocTypesCount}/${allDocumentTypes.length})`} />
        </Tabs>
        <DialogContent>
          {/* Details Tab */}
          {saveError && <Alert severity="error" sx={{ mt: 1, mb: 2 }}>{saveError}</Alert>}
          {dialogTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                name="name"
                label="Company Name"
                value={formData.name}
                onChange={handleInputChange}
                required
                fullWidth
              />
              <TextField
                name="code"
                label="Invite Code"
                value={formData.code}
                onChange={handleInputChange}
                required
                fullWidth
                helperText="Users will enter this code during signup"
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Type"
                  MenuProps={{ sx: { zIndex: 'var(--z-modal-components)' } }}
                >
                  <MenuItem value=""><em>Not Set</em></MenuItem>
                  <MenuItem value="clinic">Clinic / Practice</MenuItem>
                  <MenuItem value="individual">Individual Therapist</MenuItem>
                  <MenuItem value="test">Test Use Only</MenuItem>
                  <MenuItem value="group">Therapy Group</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                }
                label="Active"
              />
              {billingPolicy.enabled && <FormControlLabel
                control={
                  <Switch
                    name="requiresPayment"
                    disabled={!billingPolicy.enabled}
                    checked={formData.requiresPayment}
                    onChange={handleInputChange}
                  />
                }
                label={billingPolicy.enabled ? "Requires Payment (Stripe)" : "Requires Payment (billing temporarily unavailable)"}
              />}
              {billingPolicy.enabled && formData.requiresPayment && (
                <FormControlLabel
                  control={
                    <Switch
                      name="createCompanyOnSignup"
                      disabled={!billingPolicy.enabled}
                      checked={formData.createCompanyOnSignup}
                      onChange={handleInputChange}
                    />
                  }
                  label="Solo Practitioner (creates company on signup)"
                  sx={{ ml: 2 }}
                />
              )}
              {/* Read-only stats summary */}
              {editingCompany && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: 'var(--color-background-alt)', border: '1px solid var(--color-border)' }}>
                    <Box sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', mb: 0.5 }}>Users</Box>
                    <Box sx={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                      {companyStats[editingCompany.id]?.userCount || 0}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: 'var(--color-background-alt)', border: '1px solid var(--color-border)' }}>
                    <Box sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', mb: 0.5 }}>Invites Sent</Box>
                    <Box sx={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                      {companyStats[editingCompany.id]?.invitesSent || 0}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: 'var(--color-background-alt)', border: '1px solid var(--color-border)' }}>
                    <Box sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', mb: 0.5 }}>Last Login</Box>
                    <Box sx={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                      {companyStats[editingCompany.id]?.lastLogin
                        ? companyStats[editingCompany.id].lastLogin.toLocaleDateString()
                        : '—'}
                    </Box>
                  </Box>
                </Box>
              )}
              <TextField
                name="domain"
                label="Domain"
                value={formData.domain}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="email"
                label="Contact Email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="phone"
                label="Contact Phone"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          )}

          {/* Company Settings Tab */}
          {dialogTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {editingCompany && (
                <Alert
                  severity={formData.litigationHoldActive ? 'error' : 'info'}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'flex-start' }}
                    control={
                      <Switch
                        name="litigationHoldActive"
                        checked={formData.litigationHoldActive}
                        onChange={handleInputChange}
                        color="error"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Litigation Hold — all company patients
                        </Typography>
                        <Typography variant="caption" component="div">
                          Preserves transcripts for every patient in this company regardless of the global retention period. Releasing the hold makes overdue transcripts eligible at the next Secure cleanup.
                        </Typography>
                      </Box>
                    }
                  />
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 1 }}>
                These settings control which features are available to users in this company. A feature must also be enabled in System Settings to take effect. Use the role dropdown to set the minimum role required for each feature.
              </Alert>
              {/* Preset selector — apply a saved template to reset all switches */}
              {Object.keys(companyPresets).length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel>Apply Template Preset</InputLabel>
                  <Select
                    value={selectedPresetId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedPresetId(id);
                      if (id && companyPresets[id]) {
                        const preset = companyPresets[id];
                        setFormData(prev => ({
                          ...prev,
                          featureFlags: { ...defaultCompanyFeatureFlags, ...preset.featureFlags },
                          flagMinRoles: { ...defaultCompanyFlagMinRoles, ...preset.flagMinRoles },
                        }));
                      }
                    }}
                    label="Apply Template Preset"
                    MenuProps={{ sx: { zIndex: 'var(--z-modal-components)' } }}
                  >
                    <MenuItem value=""><em>— Select a preset —</em></MenuItem>
                    {sortByDisplayLabel(Object.entries(companyPresets), ([id, preset]) => preset.label || id).map(([id, preset]) => (
                      <MenuItem key={id} value={id}>
                        {preset.label}{preset.isDefault ? ' (default)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {companyFlags.map((flag) => (
                <Box key={flag.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ flex: 1, mr: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={formData.featureFlags?.[flag.key] || false}
                        onChange={handleFeatureFlagChange(flag.key)}
                      />
                    }
                    label={flag.label}
                  />
                  <Select
                    value={formData.flagMinRoles?.[flag.key] || ''}
                    onChange={(e) => handleFlagMinRoleChange(flag.key, e.target.value)}
                    size="small"
                    displayEmpty
                    sx={{ minWidth: 120, fontSize: '0.75rem' }}
                    MenuProps={{ sx: { zIndex: 'var(--z-modal-components)' } }}
                  >
                    <MenuItem value="">Any role</MenuItem>
                    {ROLE_HIERARCHY.map((role) => (
                      <MenuItem key={role} value={role}>{ROLE_LABELS[role]}+</MenuItem>
                    ))}
                  </Select>
                </Box>
              ))}
              {editingCompany && formData.featureFlags?.aiAssistant === true && missingAiUsers.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                    {missingAiUsers.length} active, role-eligible user{missingAiUsers.length === 1 ? '' : 's'} {missingAiUsers.length === 1 ? 'has' : 'have'} no AI Assistant preference.
                  </Box>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        checked={enableAiForMissingUsers}
                        onChange={(e) => setEnableAiForMissingUsers(e.target.checked)}
                      />
                    }
                    label="Enable AI Assistant for these users when saving"
                  />
                  <Box sx={{ fontSize: 'var(--text-xs)', mt: 0.5 }}>
                    Explicit opt-outs are excluded. This only fills missing legacy values for users who meet the selected minimum role.
                  </Box>
                </Alert>
              )}
            </Box>
          )}

          {/* Document Types Tab */}
          {dialogTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {docTypesLoading ? (
                <LinearProgress />
              ) : (
                <>
                  {/* Load from template preset */}
                  {Object.keys(docTypePresets).length > 0 && (
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Load from Template</InputLabel>
                      <Select
                        value={selectedDocTypePresetId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setSelectedDocTypePresetId(id);
                          if (id && docTypePresets[id]) {
                            const preset = docTypePresets[id];
                            setFormData(prev => ({
                              ...prev,
                              enabledDocumentTypes: preset.enabledDocumentTypes === null
                                ? null
                                : Array.isArray(preset.enabledDocumentTypes)
                                  ? [...preset.enabledDocumentTypes]
                                  : null,
                            }));
                          }
                        }}
                        label="Load from Template"
                        MenuProps={{ sx: { zIndex: 'var(--z-modal-components)' } }}
                      >
                        <MenuItem value=""><em>— Select a preset —</em></MenuItem>
                        {sortByDisplayLabel(Object.entries(docTypePresets), ([id, preset]) => preset.label || id).map(([id, preset]) => (
                          <MenuItem key={id} value={id}>
                            {preset.label}{preset.isDefault ? ' (default)' : ''}
                            {preset.description ? ` — ${preset.description}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                      {formData.enabledDocumentTypes === null 
                        ? 'All document types enabled (open default)' 
                        : formData.enabledDocumentTypes.length === 0
                          ? 'No document types enabled'
                          : `${formData.enabledDocumentTypes.length} of ${allDocumentTypes.length} types enabled`
                      }
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={handleSelectAllDocTypes}>Select All</Button>
                      <Button size="small" onClick={handleClearAllDocTypes} color="warning">Clear All</Button>
                    </Box>
                  </Box>

                  {formData.enabledDocumentTypes !== null && formData.enabledDocumentTypes.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      This company has no document types enabled. Users will not be able to create any documents.
                    </Alert>
                  )}

                  {DOC_TYPE_SUBCATEGORIES.map(sub => {
                    const group = docTypesBySubcategory[sub.value];
                    if (!group || group.types.length === 0) return null;
                    const groupIds = group.types.map(t => t.id);
                    const enabledInGroup = groupIds.filter(id => isDocTypeEnabled(id)).length;
                    const allChecked = enabledInGroup === groupIds.length;
                    const someChecked = enabledInGroup > 0 && !allChecked;

                    return (
                      <Accordion 
                        key={sub.value} 
                        defaultExpanded
                        disableGutters
                        sx={{ 
                          boxShadow: 'none',
                          border: '1px solid var(--color-border)',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': { margin: 0 },
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ChevronDown size={16} />}
                          sx={{ 
                            minHeight: 40,
                            '&.Mui-expanded': { minHeight: 40 },
                            '.MuiAccordionSummary-content': { margin: '4px 0', alignItems: 'center', gap: 1 },
                            '.MuiAccordionSummary-content.Mui-expanded': { margin: '4px 0' },
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={allChecked}
                            indeterminate={someChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleSubcategoryToggle(sub.value)}
                            sx={{ p: 0, mr: 0.5 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                            {sub.label}
                          </Typography>
                          <Chip 
                            label={`${enabledInGroup}/${groupIds.length}`} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              backgroundColor: allChecked ? 'var(--color-success-bg)' : someChecked ? 'var(--color-warning-bg)' : 'var(--color-error-bg)',
                              color: allChecked ? 'var(--color-success-text)' : someChecked ? 'var(--color-warning-text)' : 'var(--color-error-text)',
                            }} 
                          />
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                          {group.types.map(type => (
                            <Box key={type.id} sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={isDocTypeEnabled(type.id)}
                                    onChange={() => handleDocTypeToggle(type.id)}
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">{type.name}</Typography>
                                    {type.format && (
                                      <Chip 
                                        label={type.format} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }} 
                                      />
                                    )}
                                    {type.menu && !type.menu.includes('default') && (
                                      <Chip 
                                        label={type.menu.join(', ')} 
                                        size="small" 
                                        variant="outlined"
                                        color="warning"
                                        sx={{ height: 18, fontSize: '0.65rem' }} 
                                      />
                                    )}
                                  </Box>
                                }
                                sx={{ width: '100%' }}
                              />
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog()}>Cancel</Button>
          <Button 
            onClick={() => handleSave()}
            variant="contained" 
            disabled={isSaving || !formData.name || !formData.code}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={holdReleaseConfirmOpen}
        onClose={() => !isSaving && setHoldReleaseConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          Release company Litigation Hold?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This affects every patient in {editingCompany?.name || editingCompany?.companyName || 'this company'}.
          </Alert>
          <Typography variant="body2">
            Transcripts already older than the global retention period will become eligible for permanent deletion during the next Secure cleanup run. Deleted transcripts cannot be recovered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldReleaseConfirmOpen(false)} disabled={isSaving}>
            Keep hold
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setHoldReleaseConfirmOpen(false);
              handleSave(true);
            }}
            disabled={isSaving}
          >
            Release hold and save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
        sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
          ⚠️ Delete Company Permanently
        </DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>THIS ACTION CANNOT BE UNDONE.</strong>
          </Alert>
          
          <Box sx={{ mb: 2 }}>
            <p style={{ margin: '0 0 12px 0' }}>
              You are about to <strong>permanently delete</strong> the company:
            </p>
            <Box sx={{ 
              backgroundColor: 'var(--color-background-alt)', 
              p: 2, 
              borderRadius: 1,
              border: '1px solid var(--color-border)',
              mb: 2
            }}>
              <strong style={{ fontSize: 'var(--text-lg)' }}>{companyToDelete?.name || companyToDelete?.companyName}</strong>
              {companyToDelete?.code && (
                <span style={{ marginLeft: '12px', color: 'var(--color-text-secondary)' }}>
                  (Code: {companyToDelete.code})
                </span>
              )}
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This will NOT delete users associated with this company. 
            They will become orphaned and may lose access to certain features. 
            Consider deactivating the company instead.
          </Alert>

          <p style={{ margin: '0 0 8px 0' }}>
            To confirm deletion, type the company name exactly as shown above:
          </p>
          <TextField
            fullWidth
            placeholder={companyToDelete?.name || companyToDelete?.companyName || ''}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={isDeleting}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== (companyToDelete?.name || companyToDelete?.companyName)}
            helperText={deleteConfirmText.length > 0 && deleteConfirmText !== (companyToDelete?.name || companyToDelete?.companyName) ? 'Name does not match' : ''}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              if (!companyToDelete) return;
              setDeleteError('');
              setIsDeleting(true);
              try {
                await deleteData('companies', companyToDelete.id);
                setCompanies(prev => prev.filter(c => c.id !== companyToDelete.id));
                setDeleteDialogOpen(false);
                setCompanyToDelete(null);
                setDeleteConfirmText("");
                notifySuccess('Company deleted.');
              } catch (error) {
                console.error("Error deleting company:", error);
                setDeleteError('Could not delete the company. Check your connection and try again.');
              }
              setIsDeleting(false);
            }}
            variant="contained" 
            color="error"
            disabled={isDeleting || deleteConfirmText !== (companyToDelete?.name || companyToDelete?.companyName)}
          >
            {isDeleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog 
        open={helpDialogOpen} 
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="sm"
        sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      >
        <DialogTitle>About Companies</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <strong>Invite Codes</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--color-text-secondary)' }}>
                Users enter a company's invite code during signup. The code is stored on their account for attribution and access control.
              </p>
            </Box>
            <Box>
              <strong>Company Settings</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--color-text-secondary)' }}>
                <b>Type:</b> b2b, doctor, or marketing — used for filtering and reporting.<br />
                <b>Active:</b> Inactive companies cannot be used for new signups.
                {billingPolicy.enabled && <><br />
                <b>Requires Payment:</b> Marketing codes that require users to subscribe.<br />
                <b>Solo Practitioner:</b> When enabled with payment, creates a new company for each signup — user becomes companyAdmin of their own practice.
                </>}
              </p>
            </Box>
            <Box>
              <strong>Deleting Companies</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--color-text-secondary)' }}>
                Deleting a company is permanent and cannot be undone. Existing users who signed up with the code are unaffected — the code remains on their account.
              </p>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminCompanies;
